/**
 * 공지사항 페이지 (고객지원 > 📢 공지사항) — 2026-07-27
 *
 * 대시보드 상단 공지는 '최근 것 알림'용이라 지나가면 다시 보기 어려웠다.
 * 여기서는 본부가 올린 공지를 언제든 다시 찾아볼 수 있게 최신순으로 모아 보여준다.
 * (대시보드 노출 여부와 무관하게 게시 중인 공지 전체)
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

interface Notice {
  id: string;
  title: string;
  body: string;
  version: string | null;
  show_popup: boolean;
  show_dashboard: boolean;
  updated_at: string | null;
}

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const formatDate = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const OwnerNoticesPage: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/api/v1/owner/notices/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`공지 조회 실패 (${res.status})`);
        const data = await res.json();
        const list: Notice[] = data.notices || [];
        setNotices(list);
        if (list.length > 0) setOpenId(list[0].id); // 최신 공지는 펼친 채로
      } catch (e: any) {
        setError(e.message || '공지사항을 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📢 공지사항</h1>
        <p className="text-sm text-gray-500 mt-1">
          본부에서 안내하는 업데이트·운영 공지입니다. 최신 공지가 위에 표시됩니다.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-500">불러오는 중...</span>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{error}</div>
      )}

      {!loading && !error && notices.length === 0 && (
        <div className="text-center text-gray-500 py-16">등록된 공지사항이 없습니다.</div>
      )}

      {!loading && !error && notices.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100 shadow-sm">
          {notices.map((n) => {
            const open = openId === n.id;
            return (
              <div key={n.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : n.id)}
                  className="w-full flex items-start gap-3 px-5 py-4 text-left hover:bg-gray-50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900">{n.title}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {formatDate(n.updated_at)}
                      {n.version && <span className="ml-2">· {n.version}</span>}
                    </div>
                  </div>
                  <span className="text-gray-400 flex-shrink-0 mt-1">{open ? '▲' : '▼'}</span>
                </button>
                {open && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {n.body}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OwnerNoticesPage;
